import { a1FromRowCol, colIndexToLetters } from '../algorithms/structuralShift';
import { cfBlocksToIrEntries, rectToA1 } from '../model/conditionalFormatModel';
import type {
  XlsxCellStyle,
  XlsxCellValue,
  XlsxDataValidation,
  XlsxSheet,
  XlsxWorkbookModel,
} from '../model/types';
import type {
  XlsxCellKey,
  XlsxParsedCell,
  XlsxParsedCellStyle,
  XlsxParsedScalar,
  XlsxParsedSheet,
  XlsxParseSession,
} from './types';

const finitePositive = (value: number | undefined): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined;

/**
 * Workbook-model date policy: valid parser Date values become UTC ISO-8601
 * strings. Most OOXML dates remain numeric serials plus `style.numFmt`; this
 * conversion only covers parsers/input formats that surface a real Date and
 * keeps model cloning/serialization deterministic.
 */
function materializeScalar(value: XlsxParsedScalar): XlsxCellValue['value'] {
  if (!(value instanceof Date)) return value;
  return Number.isFinite(value.getTime()) ? value.toISOString() : null;
}

function materializeStyle(style: XlsxParsedCellStyle | undefined): XlsxCellStyle | undefined {
  if (!style) return undefined;
  const output: XlsxCellStyle = {};
  if (style.bold !== undefined) output.bold = style.bold;
  if (style.italic !== undefined) output.italic = style.italic;
  if (style.underline !== undefined) output.underline = style.underline;
  if (style.strike !== undefined) output.strike = style.strike;
  if (style.align !== undefined) output.align = style.align;
  if (style.verticalAlign !== undefined) output.verticalAlign = style.verticalAlign;
  if (style.wrapText !== undefined) output.wrapText = style.wrapText;
  if (style.textRotation !== undefined) output.textRotation = style.textRotation;
  if (style.indent !== undefined) output.indent = style.indent;
  if (style.shrinkToFit !== undefined) output.shrinkToFit = style.shrinkToFit;
  if (style.backgroundColor !== undefined) output.backgroundColor = style.backgroundColor;
  if (style.color !== undefined) output.color = style.color;
  if (style.fontFamily !== undefined) output.fontFamily = style.fontFamily;
  if (style.fontSize !== undefined) output.fontSize = style.fontSize;
  if (style.numberFormat !== undefined) output.numFmt = style.numberFormat;
  if (style.borders !== undefined) {
    output.borders = {
      ...(style.borders.top ? { top: { ...style.borders.top } } : {}),
      ...(style.borders.bottom ? { bottom: { ...style.borders.bottom } } : {}),
      ...(style.borders.left ? { left: { ...style.borders.left } } : {}),
      ...(style.borders.right ? { right: { ...style.borders.right } } : {}),
    };
  }
  return Object.keys(output).length > 0 ? output : undefined;
}

function keyPosition(key: XlsxCellKey): { row: number; col: number } | null {
  const match = /^(\d+):(\d+)$/.exec(key);
  if (!match) return null;
  const row = Number(match[1]);
  const col = Number(match[2]);
  return Number.isInteger(row) && Number.isInteger(col) ? { row, col } : null;
}

function effectiveStyle(
  sheet: XlsxParsedSheet,
  key: XlsxCellKey,
  cell: XlsxParsedCell | undefined,
): XlsxCellStyle | undefined {
  const position = cell ?? keyPosition(key);
  if (!position) return undefined;
  const parsed = sheet.styles.get(key)
    ?? sheet.rowStyles.get(position.row)
    ?? sheet.columnStyles.find(
      (entry) => position.col >= entry.min && position.col <= entry.max,
    )?.style;
  const style = materializeStyle(parsed);
  if (cell?.numberFormat && !style?.numFmt) {
    return { ...(style ?? {}), numFmt: cell.numberFormat };
  }
  return style;
}

function materializeCells(sheet: XlsxParsedSheet): Record<string, XlsxCellValue> {
  const keys = new Set<XlsxCellKey>([
    ...sheet.cells.keys(),
    ...sheet.styles.keys(),
    ...sheet.hyperlinks.keys(),
    ...sheet.notes.keys(),
  ]);
  const cells: Record<string, XlsxCellValue> = {};
  for (const key of keys) {
    const parsed = sheet.cells.get(key);
    const position = parsed ?? keyPosition(key);
    if (!position) continue;
    const cell: XlsxCellValue = { value: parsed ? materializeScalar(parsed.value) : null };
    if (parsed?.formula) cell.formula = parsed.formula.startsWith('=')
      ? parsed.formula
      : `=${parsed.formula}`;
    const style = effectiveStyle(sheet, key, parsed);
    if (style) cell.style = style;
    const hyperlink = sheet.hyperlinks.get(key);
    if (hyperlink) cell.hyperlink = { ...hyperlink };
    const note = sheet.notes.get(key);
    if (note) cell.note = { ...note };
    cells[a1FromRowCol(position.row, position.col)] = cell;
  }
  return cells;
}

function materializeValidation(
  validation: XlsxParsedSheet['features']['validations'][number],
): XlsxDataValidation | null {
  const range = validation.ranges.map(rectToA1).join(' ');
  if (!range) return null;
  return {
    range,
    type: validation.type as XlsxDataValidation['type'],
    ...(validation.staticValues ? { allowedValues: [...validation.staticValues] } : {}),
    ...(validation.formula1 !== undefined ? { formula1: validation.formula1 } : {}),
    ...(validation.formula2 !== undefined ? { formula2: validation.formula2 } : {}),
    ...(validation.operator !== undefined ? { operator: validation.operator } : {}),
    ...(validation.allowBlank !== undefined ? { allowBlank: validation.allowBlank } : {}),
    ...(validation.prompt !== undefined ? { prompt: validation.prompt } : {}),
    ...(validation.promptTitle !== undefined ? { promptTitle: validation.promptTitle } : {}),
    ...(validation.error !== undefined ? { error: validation.error } : {}),
    ...(validation.errorTitle !== undefined ? { errorTitle: validation.errorTitle } : {}),
  };
}

function materializeSheet(sheet: XlsxParsedSheet): XlsxSheet {
  const columnWidths: Record<string, number> = {};
  const hiddenColumns: Record<string, boolean> = {};
  for (const column of sheet.columns) {
    const letter = colIndexToLetters(column.index);
    const width = finitePositive(column.wch)
      ?? finitePositive(column.width)
      ?? (finitePositive(column.wpx) !== undefined ? column.wpx! / 8 : undefined);
    if (width !== undefined) columnWidths[letter] = width;
    if (column.hidden) hiddenColumns[letter] = true;
  }

  const rowHeights: Record<string, number> = {};
  const hiddenRows: Record<string, boolean> = {};
  for (const row of sheet.rows) {
    const key = String(row.index + 1);
    const height = finitePositive(row.hpt)
      ?? (finitePositive(row.hpx) !== undefined ? row.hpx! * 0.75 : undefined);
    if (height !== undefined) rowHeights[key] = height;
    if (row.hidden) hiddenRows[key] = true;
  }

  const validations = sheet.features.validations
    .map(materializeValidation)
    .filter((entry): entry is XlsxDataValidation => entry !== null);
  const conditionalFormats = cfBlocksToIrEntries(sheet.conditionalFormats).map((entry) => ({
    range: entry.range,
    rule: { ...entry.rule },
  }));
  const freeze = sheet.features.freeze;
  const output: XlsxSheet = {
    name: sheet.name,
    rowCount: Math.max(1, sheet.rowCount),
    colCount: Math.max(1, sheet.colCount),
    cells: materializeCells(sheet),
    merges: sheet.merges.map((merge) => ({ range: merge.range })),
    dataValidations: validations,
    autoFilter: sheet.features.autoFilter
      ? {
        ...sheet.features.autoFilter,
        ...(sheet.features.autoFilter.columns
          ? {
            columns: sheet.features.autoFilter.columns.map((column) => ({
              ...column,
              ...(column.values ? { values: [...column.values] } : {}),
              ...(column.custom
                ? { custom: column.custom.map((condition) => ({ ...condition })) }
                : {}),
            })),
          }
          : {}),
      }
      : null,
    conditionalFormats,
    freeze: freeze.rows > 0 || freeze.cols > 0 ? { ...freeze } : null,
    rowOutlineLevels: { ...sheet.features.rowOutlineLevels },
  };
  if (Object.keys(columnWidths).length > 0) output.columnWidths = columnWidths;
  if (Object.keys(rowHeights).length > 0) output.rowHeights = rowHeights;
  if (Object.keys(hiddenColumns).length > 0) output.hiddenColumns = hiddenColumns;
  if (Object.keys(hiddenRows).length > 0) output.hiddenRows = hiddenRows;
  return output;
}

/** Materialize every cached/lazy parser sheet into one host-neutral workbook model. */
export async function materializeXlsxWorkbookModel(
  session: XlsxParseSession,
): Promise<XlsxWorkbookModel> {
  const sheets = await Promise.all(
    session.sheetNames.map((_, index) => session.parseSheet(index).then(materializeSheet)),
  );
  const activeSheetIndex = sheets.length === 0
    ? 0
    : Math.min(Math.max(Math.trunc(session.activeSheetIndex), 0), sheets.length - 1);
  return { sheets, activeSheetIndex };
}
